import { useMutation, useQuery } from "@apollo/client/react";
import { yupResolver } from "@hookform/resolvers/yup";
import MontuToastProvider from "@repo/components/src/globalProviders/toast";
import LoadingButton from "@repo/components/src/shared/loadingButton";
import TopLayer from "@repo/components/src/shared/topLayer";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonIcon, ButtonText } from "@repo/components/src/ui/button";
import { HStack } from "@repo/components/src/ui/hstack";
import {
  Popover,
  PopoverBackdrop,
  PopoverBody,
  PopoverContent,
} from "@repo/components/src/ui/popover";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { Toast, ToastDescription } from "@repo/components/src/ui/toast";
import {
  Tooltip,
  TooltipContent,
  TooltipText,
} from "@repo/components/src/ui/tooltip";
import { VStack } from "@repo/components/src/ui/vstack";
import { BulkAssignBrandPostulationTagsDocument } from "@repo/graphql/documents/postulationTag/mutation.generated";
import { BrandPostulationTagsDocument } from "@repo/graphql/documents/postulationTag/query.generated";
import { Check, Pencil, Plus, Tag } from "lucide-react-native";
import { useCallback, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";

import { lightenHex } from "@repo/components/src/utils/color";
import { PostulationTagFragmentFragment } from "@repo/graphql/documents/postulationTag/fragment.generated";
import CreateTagForm from "./createTagForm";
import EditTagForm from "./editTagForm";
import TagBadge from "./tagBadge";

const assignTagsSchema = yup.object({
  tagIds: yup.array().of(yup.string().required()).required(),
});

type AssignTagsSchema = yup.InferType<typeof assignTagsSchema>;

type TagSelectorProps = {
  brandId: string;
  tags: readonly PostulationTagFragmentFragment[];
  hideBadges?: boolean;
} & (
  | { postulationId: string; preselectionId?: never }
  | { preselectionId: string; postulationId?: never }
);

const showErrorToast = (message: string) => {
  MontuToastProvider.show({
    placement: "bottom",
    duration: 3000,
    render: ({ id }) => (
      <Toast nativeID={`toast-${id}`} action="error" variant="solid">
        <ToastDescription>{message}</ToastDescription>
      </Toast>
    ),
  });
};

export default function TagSelector({
  brandId,
  postulationId,
  preselectionId,
  tags,
  hideBadges = false,
}: TagSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);

  const currentTagIds = useMemo(() => tags.map((t) => t.id), [tags]);

  const form = useForm<AssignTagsSchema>({
    resolver: yupResolver(assignTagsSchema),
    defaultValues: { tagIds: currentTagIds },
  });

  const { data: tagsData } = useQuery(BrandPostulationTagsDocument, {
    variables: { brandId },
  });

  const [assignTags, { loading: saving }] = useMutation(
    BulkAssignBrandPostulationTagsDocument,
    {
      onCompleted: (data) => {
        const errors = data.postulationTag.bulkAssign.userErrors;
        if (errors && errors.length > 0) {
          showErrorToast(errors[0].message);
          return;
        }
        setIsOpen(false);
        const updated = postulationId
          ? data.postulationTag.bulkAssign.postulations?.find(
              (p) => p.id === postulationId,
            )
          : data.postulationTag.bulkAssign.campaignPreselections?.find(
              (p) => p.id === preselectionId,
            );
        if (updated) {
          form.reset({
            tagIds: updated.tags.map((tag) => tag.id),
          });
        }
      },
      onError: () => {
        showErrorToast("Error al asignar tags");
      },
    },
  );

  const brandTags = useMemo(
    () => tagsData?.postulationTag.tags.map((t) => t) ?? [],
    [tagsData],
  );

  const editingTag = useMemo(
    () => brandTags.find((t) => t.id === editingTagId) ?? null,
    [editingTagId, brandTags],
  );

  const handleOpen = useCallback(() => {
    form.reset({ tagIds: currentTagIds });
    setIsCreating(false);
    setEditingTagId(null);
    setIsOpen(true);
  }, [currentTagIds, form]);

  const onSubmit = useCallback(
    (data: AssignTagsSchema) => {
      assignTags({
        variables: {
          brandId,
          postulationIds: postulationId ? [postulationId] : [],
          preselectionIds: preselectionId ? [preselectionId] : [],
          tagIds: data.tagIds,
        },
      });
    },
    [assignTags, brandId, postulationId, preselectionId],
  );

  const handleTagCreated = useCallback(
    (tagId: string) => {
      const current = form.getValues("tagIds");
      form.setValue("tagIds", [...current, tagId]);
      setIsCreating(false);
    },
    [form],
  );

  const renderBody = () => {
    if (editingTag) {
      return (
        <EditTagForm
          tagId={editingTag.id}
          initialName={editingTag.name}
          initialColor={editingTag.color}
          onDone={() => setEditingTagId(null)}
        />
      );
    } else if (isCreating) {
      return (
        <CreateTagForm
          brandId={brandId}
          onCreated={handleTagCreated}
          onCancel={() => setIsCreating(false)}
        />
      );
    } else {
      return (
        <Controller
          control={form.control}
          name="tagIds"
          render={({ field: { value, onChange } }) => (
            <VStack space="xs">
              {brandTags.map((tag) => {
                const isSelected = value.includes(tag.id);
                return (
                  <HStack key={tag.id} className="items-center" space="sm">
                    <Pressable
                      onPress={() =>
                        onChange(
                          isSelected
                            ? value.filter((id) => id !== tag.id)
                            : [...value, tag.id],
                        )
                      }
                      className="flex-1"
                    >
                      {(pressableProps) => {
                        const bgAmount = isSelected
                          ? 0.88
                          : pressableProps.hovered
                            ? 0.95
                            : 1;
                        const borderAmount = isSelected
                          ? 0.65
                          : pressableProps.hovered
                            ? 0.75
                            : 1;
                        const textAmount = isSelected
                          ? 0
                          : pressableProps.hovered
                            ? 0.2
                            : 1;
                        const isTransparent =
                          !isSelected && !pressableProps.hovered;
                        return (
                          <HStack
                            space="sm"
                            className="items-center rounded-lg px-2.5 py-2 border"
                            style={{
                              backgroundColor: isTransparent
                                ? "transparent"
                                : lightenHex(tag.color, bgAmount),
                              borderColor: isTransparent
                                ? "transparent"
                                : lightenHex(tag.color, borderAmount),
                            }}
                          >
                            <Box
                              className="w-3 h-3 rounded-full shrink-0"
                              style={{ backgroundColor: tag.color }}
                            />
                            <Text
                              size="sm"
                              className="flex-1"
                              style={{
                                color: isTransparent
                                  ? undefined
                                  : lightenHex(tag.color, textAmount),
                              }}
                              bold={isSelected}
                              numberOfLines={1}
                            >
                              {tag.name}
                            </Text>
                            {isSelected && (
                              <Check size={14} color={tag.color} />
                            )}
                          </HStack>
                        );
                      }}
                    </Pressable>
                    <Button
                      onPress={() => setEditingTagId(tag.id)}
                      action="muted"
                      variant="link"
                      size="xs"
                    >
                      <ButtonIcon as={Pencil} />
                    </Button>
                  </HStack>
                );
              })}
              {brandTags.length === 0 && (
                <Text size="xs" action="muted" className="leading-tight">
                  Los tags te ayudarán a identificar a los creadores más
                  fácilmente.
                </Text>
              )}
              <Button
                onPress={() => setIsCreating(true)}
                action="muted"
                variant="outline"
                className="border-dashed justify-start"
                rounded="lg"
                size="sm"
              >
                <ButtonIcon as={Plus} />
                <ButtonText>Nuevo tag</ButtonText>
              </Button>

              <LoadingButton
                size="sm"
                action="brand"
                rounded="lg"
                loading={saving}
                onPress={() => form.handleSubmit(onSubmit)()}
              >
                Guardar
              </LoadingButton>
            </VStack>
          )}
        />
      );
    }
  };

  return (
    <Box>
      <HStack space="sm" className="flex-wrap items-center">
        {!hideBadges &&
          tags.map((tag) => {
            return <TagBadge key={tag.id} name={tag.name} color={tag.color} />;
          })}
        <Popover
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onOpen={handleOpen}
          trigger={(triggerProps) =>
            hideBadges ? (
              <Tooltip
                trigger={(tooltipProps) => (
                  <Box {...tooltipProps}>
                    <Button
                      {...triggerProps}
                      size="sm"
                      variant="solid"
                      action="muted"
                      rounded="full"
                      onPress={handleOpen}
                      className="bg-background-0/90 hover:bg-background-0"
                    >
                      <ButtonIcon
                        as={Tag}
                        size="xs"
                        className="text-typography-700"
                      />
                    </Button>
                  </Box>
                )}
              >
                <TooltipContent>
                  <TooltipText>Tags</TooltipText>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button
                {...triggerProps}
                size="xs"
                variant="outline"
                action="muted"
                rounded="full"
                onPress={handleOpen}
              >
                <ButtonIcon as={Tag} size="xs" />
                <ButtonText className="text-xs">Tags</ButtonText>
              </Button>
            )
          }
          placement="right bottom"
        >
          <PopoverBackdrop />
          <PopoverContent className="w-72">
            <PopoverBody>
              <TopLayer>
                <Text bold size="sm">
                  Tags
                </Text>
                <Box className="mt-2">{renderBody()}</Box>
              </TopLayer>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </HStack>
    </Box>
  );
}
